import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name.split(" ")[0]}.`);
      nav(u.role === "admin" ? "/hologram-club/admin" : "/hologram-club/my-reservations", { replace: true });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const google = () => {
    const redirect = window.location.origin + "/auth-callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <AuthShell title="Enter the Club.">
      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
        <AuthField id="l-email" testId="login-email" label="Email" type="email" value={email} onChange={setEmail} />
        <AuthField id="l-password" testId="login-password" label="Password" type="password" value={password} onChange={setPassword} />
        <button type="submit" disabled={loading} className="btn-neon w-full disabled:opacity-60" data-testid="login-submit">
          {loading ? "Transmitting..." : "Log In"}
        </button>
      </form>
      <OrSeparator />
      <button onClick={google} className="btn-ghost w-full" data-testid="login-google">
        Continue with Google
      </button>
      <div className="mt-6 text-center font-mono-ui text-[11px] tracking-[0.22em] uppercase text-white/50">
        New here?{" "}
        <Link to="/hologram-club/signup" className="text-[#00E5FF] hover:text-white" data-testid="login-to-signup">
          Create account
        </Link>
      </div>
    </AuthShell>
  );
}

export function AuthShell({ title, children }) {
  return (
    <div className="relative min-h-screen min-h-[100dvh] bg-[#050505] flex items-center justify-center px-4 py-8 overflow-y-auto">
      <div className="absolute inset-0 opacity-40">
        <img src="https://images.unsplash.com/photo-1765279113604-efe3b0301134" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black to-black" />
        <div className="absolute inset-0 scanlines opacity-40" />
      </div>
      <div className="relative w-full max-w-md card-surface gradient-border p-8 md:p-10">
        <Link to="/hologram-club" className="inline-flex items-center gap-2 font-mono-ui text-[10px] tracking-[0.28em] uppercase text-white/50 hover:text-[#00E5FF]" data-testid="auth-back">
          <ArrowLeft size={12} /> Back to Club
        </Link>
        <div className="mt-6 font-mono-ui text-[11px] tracking-[0.28em] uppercase text-[#00E5FF]">Hologram Club / Access</div>
        <h1 className="mt-2 font-display font-black tracking-tighter text-4xl">{title}</h1>
        {children}
      </div>
    </div>
  );
}

export function AuthField({ id, testId, label, type = "text", value, onChange }) {
  return (
    <div>
      <label htmlFor={id} className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-white/50">
        {label}
      </label>
      <input
        id={id}
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        autoComplete={type === "password" ? "current-password" : type === "email" ? "email" : "on"}
        className="mt-2 w-full bg-[#050505] border border-white/15 text-white p-3 font-mono-ui text-sm focus:border-[#00E5FF] focus:outline-none"
      />
    </div>
  );
}

export function OrSeparator() {
  return (
    <div className="my-6 flex items-center gap-3">
      <div className="h-px bg-white/10 flex-1" />
      <span className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-white/40">or</span>
      <div className="h-px bg-white/10 flex-1" />
    </div>
  );
}
